import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetch, getYardNightMode } from "services/orders.service";
import { Order } from "types/Order";


// Mirrors the getYardNightMode response; `available` gates whether the
// feature is switched on for this site at all.
interface NightMode {
  active: boolean;
  expires_at: string | null;
  end_hour: number;
  available: boolean;
}

interface OrdersState {
  ordersCollection: Order[];
  isLoading: boolean;
  isError: boolean;
  errMsg: string | null;
  queryValue: string | null;
  filtersValue: Record<string, any>;
  nightMode: NightMode | null;
}

interface SetOrdersPayload {
  ordersCollection: Order[];
}

interface SetErrorPayload {
  errMsg: string;
}

interface SetFilterValuePayload {
  key: string;
  value: any;
}

const initialState: OrdersState = {
  ordersCollection: [],
  isLoading: false,
  isError: false,
  errMsg: null,
  queryValue: null,
  filtersValue: {},
  nightMode: null,
};

const OrdersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setLoading(state) {
      state.isLoading = true;
      state.isError = false;
    },
    setOrders(state, action: PayloadAction<SetOrdersPayload>) {
      state.isLoading = false;
      state.ordersCollection = action.payload.ordersCollection;
    },
    setError(state, action: PayloadAction<SetErrorPayload>) {
      state.isError = true;
      state.errMsg = action.payload.errMsg;
    },
    setFilterValue(state, action: PayloadAction<SetFilterValuePayload>) {
      const { key, value } = action.payload;
      state.filtersValue[key] = value;
    },
    setNightMode(state, action: PayloadAction<NightMode>) {
      state.nightMode = action.payload;
    },
  },
});

export const { setLoading, setOrders, setError, setFilterValue, setNightMode } =
  OrdersSlice.actions;

export default OrdersSlice.reducer;

export const fetchOrders =
  () =>
  async (dispatch: any) => {
    dispatch(setLoading());
    try {
      const ordersCollection = await fetch();
      dispatch(setOrders({ ordersCollection }));
    } catch (e) {
      dispatch(setError({ errMsg: "unexpected_error" }));
    }
  };

// Polled on the same 10s interval as fetchOrders - a second timer would just
// double the request rate for no benefit, and an older backend without this
// endpoint should never spam the console, so failures are swallowed.
export const fetchNightMode =
  () =>
  async (dispatch: any) => {
    try {
      const nightMode = await getYardNightMode();
      dispatch(setNightMode(nightMode));
    } catch (e) {
      // Defensive: no banner is a safe fallback when the endpoint is missing.
    }
  };
